import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { InterventionTemplateResponse } from '../../api/generated';
import type { DraftWeek } from './templateDraftTypes';
import { WeekSection } from './weekEditor/WeekSection';
import styles from './WeekEditorPanel.module.css';

interface WeekEditorPanelProps {
  weeks: DraftWeek[];
  catalog: InterventionTemplateResponse[];
  strokeColor: string;
  fillColor: string;
  fieldErrors: Record<string, string>;
  clearFieldError: (path: string) => void;
  onChange: (weeks: DraftWeek[]) => void;
}

export const WeekEditorPanel = ({
  weeks,
  catalog,
  strokeColor,
  fillColor,
  fieldErrors,
  clearFieldError,
  onChange
}: WeekEditorPanelProps) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleUpdateWeek = (index: number, updated: DraftWeek) => {
    const next = [...weeks];
    next[index] = updated;
    onChange(next);
  };

  const handleRemoveWeek = (index: number) => {
    onChange(weeks.filter((_, i) => i !== index));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIdx = weeks.findIndex((w) => w._key === active.id);
    const newIdx = weeks.findIndex((w) => w._key === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onChange(arrayMove(weeks, oldIdx, newIdx));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={weeks.map((w) => w._key)} strategy={verticalListSortingStrategy}>
        <div className={styles.panel}>
          {weeks.map((week, idx) => (
            <WeekSection
              key={week._key}
              week={week}
              weekIndex={idx}
              catalog={catalog}
              strokeColor={strokeColor}
              fillColor={fillColor}
              fieldErrors={fieldErrors}
              clearFieldError={clearFieldError}
              onUpdate={(updated) => handleUpdateWeek(idx, updated)}
              onRemove={() => handleRemoveWeek(idx)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
